import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { Bell, BellOff, BellRing, Check, X, CalendarClock } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { subscribeToPush, getSubscriptionStatus } from "@/lib/push";
import { toast } from "sonner";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushStatus, setPushStatus] = useState("unsupported");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  }, []);

  useEffect(() => {
    load();
    getSubscriptionStatus().then(setPushStatus);
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const enablePush = async () => {
    try {
      await subscribeToPush();
      setPushStatus("subscribed");
      toast.success("Push notifications enabled — try the test below");
    } catch (err) {
      toast.error(err?.message || fmtError(err));
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(data.sent > 0 ? "Test push sent" : "No subscribed devices yet");
    } catch (err) { toast.error(fmtError(err)); }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };
  const markAll = async () => {
    try {
      await api.post(`/notifications/read-all`);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-10 w-10 grid place-items-center rounded-2xl hover:bg-muted/70 tap"
          data-testid="notif-bell"
          aria-label="Notifications"
        >
          {unread > 0 ? <BellRing className="h-4.5 w-4.5 text-primary" /> : <Bell className="h-4.5 w-4.5 text-muted-foreground" />}
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] rounded-2xl p-0 overflow-hidden" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
          <p className="font-display text-base font-extrabold tracking-tight">Notifications</p>
          {unread > 0 && (
            <button onClick={markAll} className="text-[10px] uppercase tracking-wider font-bold text-primary hover:underline" data-testid="notif-mark-all">
              Mark all read
            </button>
          )}
        </div>

        {pushStatus !== "subscribed" && pushStatus !== "unsupported" && (
          <div className="px-4 py-3 bg-primary-50 border-b border-primary-100">
            <p className="text-xs font-semibold text-primary-700">Get reminders on your phone</p>
            <p className="text-[11px] text-primary-700/80 mt-1 mb-2">Enable browser notifications to never miss a follow-up — works when this tab is closed.</p>
            <Button size="sm" className="rounded-xl h-8 tap" onClick={enablePush} data-testid="enable-push">
              <BellRing className="h-3 w-3 mr-1" /> Enable on this device
            </Button>
          </div>
        )}
        {pushStatus === "subscribed" && (
          <div className="px-4 py-2 border-b border-border/60 flex items-center justify-between text-[11px]">
            <span className="text-emerald-700 font-semibold flex items-center gap-1"><Check className="h-3 w-3" /> Push enabled on this device</span>
            <button onClick={sendTest} className="text-primary font-semibold hover:underline" data-testid="push-test">
              Send test
            </button>
          </div>
        )}
        {pushStatus === "denied" && (
          <div className="px-4 py-2 border-b border-border/60 text-[11px] text-red-700 flex items-center gap-1">
            <BellOff className="h-3 w-3" /> Notifications blocked. Enable in browser settings.
          </div>
        )}

        <div className="max-h-[400px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No notifications yet.</p>
          ) : items.map((n) => (
            <div
              key={n.id}
              className={`px-4 py-3 border-b border-border/40 flex items-start gap-3 ${!n.read_at ? "bg-primary-50/40" : ""}`}
              data-testid={`notif-${n.id}`}
            >
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${!n.read_at ? "bg-primary" : "bg-transparent"}`} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm leading-tight">{n.title}</p>
                {n.body && <p className="text-xs text-muted-foreground mt-0.5 truncate">{n.body}</p>}
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</p>
              </div>
              {!n.read_at && (
                <button onClick={() => markRead(n.id)} className="h-6 w-6 rounded-full hover:bg-muted grid place-items-center tap shrink-0" aria-label="Mark read" data-testid={`notif-read-${n.id}`}>
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>
          ))}
        </div>

        <Link
          to="/calendar"
          onClick={() => setOpen(false)}
          className="block px-4 py-3 text-center text-xs font-semibold text-primary hover:bg-muted/50 border-t border-border/60 tap"
          data-testid="notif-open-calendar"
        >
          <CalendarClock className="h-3 w-3 inline-block mr-1 -mt-0.5" /> Open calendar
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
